import * as React from 'react';
import { Button, Icon } from 'semantic-ui-react';
import { tl } from 'common/services/i18nService';
import './responsive.scss';
import newAuthorUIService from 'author/src/services/newAuthorUIService';
import { decodeDisplayText } from 'common/util/text/textUtil';
import * as styles from './FeatureOverlay.scss';


interface OverlayFrameProps {
    children: React.ReactNode;
    onClose: () => void;
    text?: string;
    secondaryText?: string;
    isNotClosable?: boolean;
    iconTitle?: React.ReactElement;
    actionButtonRight?: React.ReactElement;
    isMidsizedOverlay?: any;
    backBtnComponent?: React.ReactElement;
    dynamicOverlayTitle?: React.ReactElement;
    withoutIcon?: boolean;
    closeButtonClasses?: string;
    removeAnimationClasses?: string;
}
interface Header {
    key: string;
    text: string;
}
interface ChildComponentProps {
    getHeader?: (header: Header[]) => void;
}
interface HeaderProps {
    headerTitles: { key: string; text: string }[];
}
const Header: React.FC<HeaderProps> = ({ headerTitles }) => {
    if (!headerTitles || headerTitles.length === 0) return null;

    return (
        <div className={styles.headerNote}>
            <h5>
                {headerTitles.map(({ key, text }, index) => (
                    <React.Fragment key={key}>
                        {decodeDisplayText(text.trim())}
                        {index < headerTitles.length - 1 && <br />}
                    </React.Fragment>
                ))}
            </h5>
        </div>
    );
};
export const OverlayFrame: React.FC<OverlayFrameProps> = ({
    backBtnComponent,
    iconTitle,
    text,
    secondaryText,
    actionButtonRight,
    isNotClosable,
    isMidsizedOverlay,
    onClose,
    children,
    dynamicOverlayTitle,
    withoutIcon,
    closeButtonClasses,
    removeAnimationClasses,
}) => {
    const title = dynamicOverlayTitle ?? text;
    const [header, setHeader] = React.useState<Header[] | null>(null);
    const getHeader = (newHeader: Header[]) => {
        setHeader(newHeader);
    };
    const renderChildrenWithProps = React.Children.map(children, (child) => {
        if (React.isValidElement<ChildComponentProps>(child)) {
            return React.cloneElement(child, { getHeader });
        }
        return child;
    });

    const shouldShowOldPrintManagerStyle = (title: string) => !(newAuthorUIService.isNewAuthorUIEnabled() && title.includes('print'));

    return (
        <React.Fragment>
            {shouldShowOldPrintManagerStyle(text)
                && (
                    <>
                        <div className="featureOverlayHeader">
                            <div id="overlayTitle" className="subHeader" style={{ alignItems: 'center' }}>
                                <div>
                                    {backBtnComponent}
                                    {iconTitle && (
                                        <div className={styles.iconMargin}>{iconTitle}</div>
                                    )}
                                    {title && (
                                        <h2>{title}</h2>
                                    )}
                                </div>
                                <span>{secondaryText}</span>
                            </div>
                            <div className={styles.rightSideBox}>
                                {actionButtonRight && (
                                    <div className={styles.iconMargin}>{actionButtonRight}</div>
                                )}
                                {!isNotClosable && (
                                    isMidsizedOverlay
                                        ? (
                                            <Button
                                                className={styles.transparentButton}
                                                onClick={onClose}
                                                aria-label={tl('common.close')}
                                            >
                                                <Icon name="times" />
                                            </Button>
                                        )
                                        : (
                                            <Button
                                                size="large"
                                                onClick={onClose}
                                                color="grey"
                                                className={`overlayCloseButton ${closeButtonClasses || ''}`}
                                                aria-label={tl('common.close')}
                                            >
                                                {!withoutIcon && <Icon name="times" />}
                                                {tl('common.close')}
                                            </Button>
                                        )
                                )}
                            </div>
                        </div>
                        <Header headerTitles={header} />
                    </>
                )}
            <div className={`${styles.overlay} ${removeAnimationClasses || ''}`}>
                {renderChildrenWithProps}
            </div>
        </React.Fragment>
    );
};
